// Firestore 服務
import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';

export interface Policy {
  id: string;
  name: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
  annualPremium?: number;
  company?: string;
  monthlyPremium?: number;
  category?: string;
  policyNumber?: string;
  coverage?: string;
  effectiveDate?: string;
  expiryDate?: string; // 新增：保單到期日期
  deductible?: string;
  copay?: string;
  benefitAmount?: string;
  waitingPeriod?: string;
  petName?: string;
  annualLimit?: string;
  liabilityLimit?: string;
  annualMaximum?: string;
  preventiveCoverage?: string;
  basicCoverage?: string;
  majorCoverage?: string;
  hospitalBenefit?: string;
  weeklyBenefit?: string;
  pdfUrl?: string;
  duration?: string;
  // 新增用戶相關字段
  userId?: string;
  fileName?: string;
  filePath?: string;
  fileSize?: number;
  uploadTime?: Date;
  // 新增保費單位和狀態字段
  premiumUnit?: 'monthly' | 'one-time'; // 保費單位：月繳或一次性
  status?: 'active' | 'expired'; // 保單狀態：保障中或已到期
  analysisResult?: any;
  confidence?: number;
  // 新增：完整PDF文本內容（用於AI回答問題）
  fullText?: string; // 完整的PDF提取文本，可能被截斷以符合Firestore大小限制
}

export class FirestoreService {
  async getAllPolicies(): Promise<Policy[]> {
    try {
      console.log('🔄 從 Firestore 載入保單數據...');
      const policiesRef = collection(db, 'policies');
      const policiesSnapshot = await getDocs(policiesRef);
      
      if (policiesSnapshot.empty) {
        console.log('⚠️ Firestore 中沒有保單數據');
        return [];
      }
      
      const policies: Policy[] = [];
      policiesSnapshot.forEach((doc) => {
        const data = doc.data();
        policies.push({
          id: doc.id,
          name: data.name || '未知保單',
          description: data.description || '無描述',
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          annualPremium: data.annualPremium || 0,
          company: data.company || '未知公司',
          monthlyPremium: data.monthlyPremium || 0,
          category: data.category || '未知類別',
          policyNumber: data.policyNumber || '',
          coverage: data.coverage || '',
          effectiveDate: data.effectiveDate || '',
          deductible: data.deductible || '',
          copay: data.copay || '',
          benefitAmount: data.benefitAmount || '',
          waitingPeriod: data.waitingPeriod || '',
          petName: data.petName || '',
          annualLimit: data.annualLimit || '',
          liabilityLimit: data.liabilityLimit || '',
          annualMaximum: data.annualMaximum || '',
          preventiveCoverage: data.preventiveCoverage || '',
          basicCoverage: data.basicCoverage || '',
          majorCoverage: data.majorCoverage || '',
          hospitalBenefit: data.hospitalBenefit || '',
          weeklyBenefit: data.weeklyBenefit || '',
          duration: data.duration || ''
        });
      });
      
      console.log(`✅ 成功載入 ${policies.length} 筆保單數據`);
      return policies;
      
    } catch (error) {
      console.error('❌ 載入保單數據失敗:', error);
      // 返回空數組而不是拋出錯誤
      return [];
    }
  }
  
  async getPolicyById(policyId: string): Promise<Policy | null> {
    try {
      const policyRef = doc(db, 'policies', policyId);
      const policySnap = await getDoc(policyRef);
      
      if (policySnap.exists()) {
        const data = policySnap.data();
        return {
          id: policySnap.id,
          name: data.name || '未知保單',
          description: data.description || '無描述',
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
          annualPremium: data.annualPremium || 0,
          company: data.company || '未知公司',
          monthlyPremium: data.monthlyPremium || 0,
          category: data.category || '未知類別',
          policyNumber: data.policyNumber || '',
          coverage: data.coverage || '',
          effectiveDate: data.effectiveDate || '',
          deductible: data.deductible || '',
          copay: data.copay || '',
          benefitAmount: data.benefitAmount || '',
          waitingPeriod: data.waitingPeriod || '',
          petName: data.petName || '',
          annualLimit: data.annualLimit || '',
          liabilityLimit: data.liabilityLimit || '',
          annualMaximum: data.annualMaximum || '',
          preventiveCoverage: data.preventiveCoverage || '',
          basicCoverage: data.basicCoverage || '',
          majorCoverage: data.majorCoverage || '',
          hospitalBenefit: data.hospitalBenefit || '',
          weeklyBenefit: data.weeklyBenefit || '',
          duration: data.duration || ''
        };
      } else {
        console.log('❌ 找不到指定的保單');
        return null;
      }
    } catch (error) {
      console.error('❌ 載入保單詳情失敗:', error);
      return null;
    }
  }

  // 保存用戶政策到Firestore
  async saveUserPolicy(userId: string, policyData: Partial<Policy>): Promise<string> {
    try {
      console.log('保存用戶政策到Firestore:', userId);
      
      const policyRef = collection(db, 'users', userId, 'policies');
      const docRef = await addDoc(policyRef, {
        ...policyData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        userId
      });
      
      console.log('政策保存成功，ID:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('保存政策失敗:', error.message);
      throw new Error(`保存政策失敗: ${error.message}`);
    }
  }

  // 獲取用戶的所有政策
  async getUserPolicies(userId: string): Promise<Policy[]> {
    try {
      console.log('獲取用戶政策:', userId);
      
      const policiesRef = collection(db, 'users', userId, 'policies');
      const q = query(policiesRef, orderBy('createdAt', 'desc'));
      const policiesSnapshot = await getDocs(q);
      
      if (policiesSnapshot.empty) {
        console.log('用戶沒有政策數據');
        return [];
      }
      
      const policies: Policy[] = [];
      policiesSnapshot.forEach((doc) => {
        const data = doc.data();
        policies.push({
          id: doc.id,
          name: data.name || '未知保單',
          description: data.description || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          annualPremium: data.annualPremium,
          company: data.company,
          monthlyPremium: data.monthlyPremium,
          category: data.category,
          policyNumber: data.policyNumber,
          coverage: data.coverage,
          effectiveDate: data.effectiveDate,
          expiryDate: data.expiryDate, // 新增：讀取到期日期
          deductible: data.deductible,
          copay: data.copay,
          benefitAmount: data.benefitAmount,
          waitingPeriod: data.waitingPeriod,
          petName: data.petName,
          annualLimit: data.annualLimit,
          liabilityLimit: data.liabilityLimit,
          annualMaximum: data.annualMaximum,
          preventiveCoverage: data.preventiveCoverage,
          basicCoverage: data.basicCoverage,
          majorCoverage: data.majorCoverage,
          hospitalBenefit: data.hospitalBenefit,
          weeklyBenefit: data.weeklyBenefit,
          pdfUrl: data.pdfUrl,
          duration: data.duration,
          userId: data.userId,
          fileName: data.fileName,
          filePath: data.filePath,
          fileSize: data.fileSize,
          uploadTime: data.uploadTime?.toDate(),
          analysisResult: data.analysisResult,
          confidence: data.confidence,
          // 新增保費單位和狀態字段
          premiumUnit: data.premiumUnit,
          status: data.status,
          // 新增：完整PDF文本內容
          fullText: data.fullText
        });
      });
      
      console.log(`獲取到 ${policies.length} 個用戶政策`);
      
      // 輸出保單 ID 用於調試
      if (policies.length > 0) {
        console.log('📋 保單列表:');
        policies.forEach(p => {
          console.log(`  - ID: ${p.id}, 名稱: ${p.name}, fullText: ${p.fullText ? '有' : '無'}`);
        });
      }
      
      return policies;
    } catch (error: any) {
      console.error('獲取用戶政策失敗:', error.message);
      throw new Error(`獲取用戶政策失敗: ${error.message}`);
    }
  }

  // 更新用戶政策
  async updateUserPolicy(userId: string, policyId: string, updates: Partial<Policy>): Promise<void> {
    try {
      console.log('更新用戶政策:', userId, policyId);
      
      const policyRef = doc(db, 'users', userId, 'policies', policyId);
      await updateDoc(policyRef, {
        ...updates,
        updatedAt: Timestamp.now()
      });
      
      console.log('政策更新成功');
    } catch (error: any) {
      console.error('更新政策失敗:', error.message);
      throw new Error(`更新政策失敗: ${error.message}`);
    }
  }

  // 刪除用戶政策
  async deleteUserPolicy(userId: string, policyId: string): Promise<void> {
    try {
      console.log('刪除用戶政策:', userId, policyId);
      
      const policyRef = doc(db, 'users', userId, 'policies', policyId);
      await deleteDoc(policyRef);
      
      console.log('政策刪除成功');
    } catch (error: any) {
      console.error('刪除政策失敗:', error.message);
      throw new Error(`刪除政策失敗: ${error.message}`);
    }
  }

  // 獲取單個用戶政策
  async getUserPolicy(userId: string, policyId: string): Promise<Policy | null> {
    try {
      console.log('獲取單個用戶政策:', userId, policyId);
      
      const policyRef = doc(db, 'users', userId, 'policies', policyId);
      const policySnap = await getDoc(policyRef);
      
      if (!policySnap.exists()) {
        console.log('政策不存在');
        return null;
      }
      
      const data = policySnap.data();
      return {
        id: policySnap.id,
        name: data.name || '未知保單',
        description: data.description || '',
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        annualPremium: data.annualPremium,
        company: data.company,
        monthlyPremium: data.monthlyPremium,
        category: data.category,
        policyNumber: data.policyNumber,
        coverage: data.coverage,
        effectiveDate: data.effectiveDate,
        deductible: data.deductible,
        copay: data.copay,
        benefitAmount: data.benefitAmount,
        waitingPeriod: data.waitingPeriod,
        petName: data.petName,
        annualLimit: data.annualLimit,
        liabilityLimit: data.liabilityLimit,
        annualMaximum: data.annualMaximum,
        preventiveCoverage: data.preventiveCoverage,
        basicCoverage: data.basicCoverage,
        majorCoverage: data.majorCoverage,
        hospitalBenefit: data.hospitalBenefit,
        weeklyBenefit: data.weeklyBenefit,
        pdfUrl: data.pdfUrl,
        duration: data.duration,
        userId: data.userId,
        fileName: data.fileName,
        filePath: data.filePath,
        fileSize: data.fileSize,
        uploadTime: data.uploadTime?.toDate(),
        analysisResult: data.analysisResult,
        confidence: data.confidence
      };
    } catch (error: any) {
      console.error('獲取政策失敗:', error.message);
      throw new Error(`獲取政策失敗: ${error.message}`);
    }
  }
}

export const firestoreService = new FirestoreService();
